import { ChangeDetectionStrategy, Component, ElementRef, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { InternationalizationService } from '@examdojo/core/i18n';
import { RiveDirective } from '@examdojo/ui/rive';
import { TranslocoPipe } from '@jsverse/transloco';
import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import {
  combineLatest,
  concat,
  defer,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  scan,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';

gsap.registerPlugin(SplitText);

@Component({
  selector: 'dojo-loading-sensei',
  imports: [RiveDirective, TranslocoPipe],
  templateUrl: './loading-sensei.component.html',
  styleUrl: './loading-sensei.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'relative flex flex-col items-center justify-center' },
})
export class LoadingSenseiComponent {
  constructor(private readonly internationalizationService: InternationalizationService) {
    this.createMessageAnimation().pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly messageText = viewChild<ElementRef<HTMLElement>>('messageText');
  private readonly messageElement$ = toObservable(this.messageText).pipe(
    filter(Boolean),
    map((ref) => ref.nativeElement),
  );

  private readonly TOTAL_DYNAMIC_MESSAGES = 8;
  private readonly TOTAL_STATIC_MESSAGES = 17;

  readonly staticMessageKey = `grading.loading_messages.static.${
    Math.floor(Math.random() * this.TOTAL_STATIC_MESSAGES) + 1
  }`;

  private generateDynamicMessage(): Observable<string> {
    return concat(
      of(`grading.loading_messages.dynamic.1`),
      timer(0, Math.floor(Math.random() * 2000) + 3000).pipe(
        scan((currentIndex) => {
          return Math.min(currentIndex + 1, this.TOTAL_DYNAMIC_MESSAGES);
        }, 1),
        takeWhile((index) => index < this.TOTAL_DYNAMIC_MESSAGES, true),
        map((messageIndex) => `grading.loading_messages.dynamic.${messageIndex}`),
      ),
    );
  }

  private createMessageAnimation(): Observable<void> {
    return combineLatest([this.generateDynamicMessage(), this.messageElement$]).pipe(
      distinctUntilChanged(),
      switchMap(([message, element]) => {
        return defer(
          () =>
            new Observable<void>((subscriber) => {
              // Kill any existing animations
              gsap.killTweensOf(element);
              const dots = document.createElement('span');

              // Animate out current text
              gsap.to(element, {
                opacity: 0,
                y: -20,
                scale: 0.95,
                duration: 0.3,
                ease: 'power2.in',
                onComplete: () => {
                  // Update text content
                  element.textContent = this.internationalizationService.translate(message);
                  dots.style.width = '24px';
                  dots.style.display = 'inline-block';
                  dots.style.textAlign = 'left';
                  dots.textContent = '   '; // Three spaces initially
                  element.appendChild(dots);

                  // Animate dots
                  gsap.to(dots, {
                    duration: 2.5,
                    ease: 'steps(3)',
                    onUpdate: function (this: gsap.core.Tween) {
                      const progress = Math.floor(this['progress']() * 3);
                      dots.textContent = '.'.repeat(progress) + ' '.repeat(3 - progress);
                    },
                    onComplete: () => {
                      dots.textContent = '...';
                    },
                  });

                  // Animate in new text
                  gsap.fromTo(
                    element,
                    {
                      opacity: 0,
                      y: 20,
                      scale: 0.95,
                    },
                    {
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      duration: 0.4,
                      ease: 'back.out(1.7)',
                      onComplete: () => {
                        subscriber.next();
                        subscriber.complete();
                      },
                    },
                  );
                },
              });
            }),
        );
      }),
    );
  }
}
